import { useState } from 'react'
import { useChainId } from 'wagmi'

import { SparkReward } from '../types'
import { useClaimableRewards } from './useClaimableRewards'
import { useSnapshot } from '@/domain/hooks/useSnapshot'
import { Token } from '@/domain/types/Token'
import { ClaimSparkRewardsObjective } from '@/features/actions/flavours/claim-spark-rewards/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { PageState, PageStatus } from '@/features/dialogs/common/types'

export interface UseClaimSparkRewardsDialogParams {
  tokensToClaim: Token[]
}

export interface UseClaimSparkRewardsDialogResult {
  pageStatus: PageStatus
  rewardsToClaim: SparkReward[]
  actionsViewState: ActionsViewState
  chainId: number
}

export function useClaimSparkRewardsDialog({
  tokensToClaim,
}: UseClaimSparkRewardsDialogParams): UseClaimSparkRewardsDialogResult {
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const { data: claimableRewards = [] } = useClaimableRewards()
  const chainId = useChainId()

  const filteredRewards = claimableRewards.filter((reward) =>
    tokensToClaim.some((token) => token.address === reward.token.address),
  )
  const objectives: ClaimSparkRewardsObjective[] = filteredRewards.map((reward) => ({
    type: 'claimSparkRewards',
    token: reward.token,
    epoch: reward.epoch,
    cumulativeAmount: reward.cumulativeAmount,
    merkleRoot: reward.merkleRoot,
    merkleProof: reward.merkleProof,
  }))
  const [rewardsToClaim] = useSnapshot(
    filteredRewards.map((reward) => ({
      token: reward.token,
      amountToClaim: reward.amountToClaim,
    })),
  )

  const actionsEnabled = true

  const { viewState: actionsViewState } = useActions({
    objectives,
    context: {
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  return {
    pageStatus: {
      state: pageStatus,
      actionsEnabled,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    rewardsToClaim,
    actionsViewState,
    chainId,
  }
}
